import { useEffect, useState } from "react";
import { LayoutTemplate, Lock, Save, TriangleAlert } from "lucide-react";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { api } from "../../api/client";
import type { ApplyTemplateMode, QuoteDetail, TemplateSummary } from "../../api/types";

// 範本對話框:**套用現有的,或把這一張存成新的。**
//
// 業主 2026-09-17:「額外發現沒有儲存範本的地方 套用範本按鈕改字為 範本 /
// 裡面包含 套用現有範本(下拉選項)以及儲存成新範本(命名)」。
//
// 兩支後端端點一直都是好的(`POST /quotes/{id}/apply-template` 與
// `POST /templates`),缺的都是畫面上的入口 —— 存範本那一支從第四輪就在,
// 而到這一輪為止沒有任何一個畫面叫得到它。
//
// **存完要看得到它出現在清單裡。** 看不到的話使用者會再存一次,
// 於是有了兩個一模一樣的範本,而範本清單本來就沒有幾個位置。
//
// 兩個選項照規格走:「取代現有章節 / 附加在後面」與「包含單價」。
// 預設是「附加在後面」+「包含單價」—— 附加不會弄壞任何東西,
// 而取代會清掉現有章節、後端當場 commit,所以取代一定要再確認一次。
//
// 這裡刻意不走 react-query:對話框要能在沒有 QueryClientProvider 的情況下
// 單獨掛起來測,而且範本清單每次開啟重取一次就夠了。

export interface ApplyTemplateDialogProps {
  open: boolean;
  /** 要套進哪一張報價單。 */
  quoteId: number;
  /** 目前的章節數,取代模式的警告要講清楚會清掉幾章。 */
  sectionCount: number;
  /** 預先選好的範本(⌘K 或範本頁帶過來)。 */
  presetTemplateId?: number | null;
  onClose: () => void;
  /** 套用成功。回傳的是後端重算後的完整報價單。 */
  onApplied: (quote: QuoteDetail) => void;
}

export function ApplyTemplateDialog({
  open,
  quoteId,
  sectionCount,
  presetTemplateId = null,
  onClose,
  onApplied,
}: ApplyTemplateDialogProps) {
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [mode, setMode] = useState<ApplyTemplateMode>("append");
  const [includePrices, setIncludePrices] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 存成新範本那一半。
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");

  // 每次開啟都重新取一次範本清單,並把選項復原成安全的預設值。
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setMode("append");
    setIncludePrices(true);
    setConfirming(false);
    setError(null);
    setNewName("");
    setSaved("");
    setLoading(true);
    api
      .get<TemplateSummary[]>("/templates")
      .then((result) => {
        if (cancelled) return;
        const list = Array.isArray(result) ? result : [];
        setTemplates(list);
        // 帶著指定範本進來就選它,否則選第一個,免得一開啟就是「什麼都沒選」。
        const preset = list.find((item) => item.id === presetTemplateId);
        setTemplateId(preset?.id ?? list[0]?.id ?? null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setTemplates([]);
        setTemplateId(null);
        setError(err instanceof Error ? err.message : "範本載入失敗,請稍後再試");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, presetTemplateId]);

  const selected = templates.find((template) => template.id === templateId) ?? null;

  const apply = async () => {
    if (templateId === null) return;
    setApplying(true);
    setError(null);
    try {
      const saved = await api.post<QuoteDetail>(`/quotes/${quoteId}/apply-template`, {
        template_id: templateId,
        include_prices: includePrices,
        mode,
      });
      onApplied(saved);
    } catch (err) {
      // 失敗時不關對話框:關掉的話使用者只會看到一則提示、卻不知道剛才選了什麼。
      setError(err instanceof Error ? err.message : "套用失敗,請稍後再試");
      setConfirming(false);
    } finally {
      setApplying(false);
    }
  };

  /**
   * 把這一張存成新範本。
   *
   * 存完**當場把清單重撈一次**:看不到自己剛存的東西,使用者會再存一次。
   * 客戶、日期、聯絡人不會被抄進範本裡 —— 那是後端的事(見 routers/templates.py)。
   */
  const saveAsTemplate = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      const made = await api.post<TemplateSummary>("/templates", {
        name,
        description: "",
        from_quote_id: quoteId,
      });
      const list = await api.get<TemplateSummary[]>("/templates");
      setTemplates(Array.isArray(list) ? list : []);
      setTemplateId(made?.id ?? null);
      setSaved(name);
      setNewName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "存不起來,請稍後再試");
    } finally {
      setSaving(false);
    }
  };

  // 取代會把現有章節整批清掉,而且後端當場 commit —— 不能一鍵毀掉使用者的工作。
  const submit = () => {
    if (templateId === null) return;
    if (mode === "replace" && !confirming) {
      setConfirming(true);
      return;
    }
    void apply();
  };

  const footer = confirming ? (
    <>
      <Button onClick={() => setConfirming(false)}>返回</Button>
      <Button variant="danger" loading={applying} onClick={() => void apply()}>
        確定取代
      </Button>
    </>
  ) : (
    <>
      <Button onClick={onClose}>取消</Button>
      <Button variant="primary" disabled={templateId === null} loading={applying} onClick={submit}>
        套用
      </Button>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="範本"
      // 二次確認時點背景不關,免得手滑之後搞不清楚到底套了沒。
      closeOnBackdrop={!confirming}
      footer={footer}
    >
      {error ? (
        <p role="alert" className="m-0 mb-3 text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}

      {confirming ? (
        <div className="flex flex-col gap-2">
          <p className="m-0 flex items-start gap-2 text-sm text-[var(--text)]">
            <TriangleAlert aria-hidden="true" size={16} className="mt-0.5 shrink-0 text-[var(--danger)]" />
            <span>
              套用「{selected?.name}」會清掉目前的 {sectionCount} 個章節與其中的所有明細,而且無法復原。
            </span>
          </p>
          <p className="m-0 text-sm text-[var(--text-2)]">
            只想保留現有內容的話,請回上一步改選「附加在後面」。
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* **下拉,不是一排單選按鈕**(業主:「套用現有範本(下拉選項)」)。
              原本每一顆都帶說明與章數,對話框長得像一頁設定 ——
              而它現在還要裝下「存成新範本」那一半。
              選中的那一個的說明改放在下拉底下,資訊一點都沒有少。 */}
          <div className="flex flex-col gap-1">
            <Select
              label="套用現有範本"
              value={templateId === null ? "" : String(templateId)}
              onChange={(event) =>
                setTemplateId(event.target.value === "" ? null : Number(event.target.value))
              }
              options={
                loading
                  ? [{ value: "", label: "載入中…" }]
                  : templates.length === 0
                    ? [{ value: "", label: "還沒有任何範本" }]
                    : templates.map((template) => ({
                        value: String(template.id),
                        label: template.name,
                      }))
              }
            />
            {selected ? (
              <p className="m-0 flex flex-wrap items-center gap-2 text-xs text-[var(--text-2)]">
                <LayoutTemplate aria-hidden="true" size={12} className="text-[var(--accent)]" />
                <span className="tabular">
                  {selected.section_count} 章 · {selected.line_count} 列
                </span>
                {selected.is_system ? (
                  <span className="inline-flex items-center gap-1 text-[var(--text-3)]">
                    <Lock aria-hidden="true" size={12} />
                    系統內建
                  </span>
                ) : null}
                {selected.description ? <span>{selected.description}</span> : null}
              </p>
            ) : null}
          </div>

          <fieldset className="m-0 flex min-w-0 flex-col gap-1.5 border-0 p-0">
            <legend className="mb-1 p-0 text-sm font-medium text-[var(--text-2)]">套用方式</legend>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--text)]">
              <input
                type="radio"
                name="nq-apply-mode"
                checked={mode === "append"}
                onChange={() => setMode("append")}
              />
              附加在後面
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--text)]">
              <input
                type="radio"
                name="nq-apply-mode"
                checked={mode === "replace"}
                onChange={() => setMode("replace")}
              />
              取代現有章節
            </label>
            {mode === "replace" ? (
              <p className="m-0 text-xs text-[var(--danger)]">
                目前的 {sectionCount} 個章節會被清掉,按下「套用」還會再確認一次。
              </p>
            ) : null}
          </fieldset>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--text)]">
            <input type="checkbox" checked={includePrices} onChange={(event) => setIncludePrices(event.target.checked)} />
            包含單價
          </label>
          <p className="m-0 -mt-3 text-xs text-[var(--text-3)]">不勾選就只帶章節與品項骨架,單價一律留 0。</p>

          {/* ---------------------------------------------- 存成新範本 */}
          <div className="flex flex-col gap-2 border-t border-[var(--border)] pt-3">
            <h3 className="m-0 text-sm font-medium text-[var(--text-2)]">儲存成新範本</h3>
            <p className="m-0 text-xs text-[var(--text-3)]">
              把這一張的章節與明細存起來,下次直接套用。客戶、日期與聯絡人不會被存進去。
            </p>
            <div className="flex flex-wrap items-end gap-2">
              {/* **會長大的是外面這一層,不是 input。**

                  `Input` 的 `className` 掛在 <input> 本身上,而 input 的父層是
                  `flex-col` —— 在直的那條軸上 `flex: 1` 管的是**高度**,
                  於是輸入框的高度被 flex 接管、`h-9` 不算數;而真正該長大的那層
                  容器又沒有 `flex-1`,只縮到內容寬。兩件事加起來就是業主看到的
                  「輸入框大小不對」(2026-09-17)。 */}
              <div data-field="new-template-name" className="min-w-[12rem] flex-1">
                <Input
                  label="新範本名稱"
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                />
              </div>
              <Button
                loading={saving}
                // **沒有命名就存不下去。** 一堆叫「未命名」的範本等於沒有範本。
                disabled={newName.trim() === ""}
                icon={<Save aria-hidden="true" size={16} />}
                onClick={() => void saveAsTemplate()}
              >
                儲存成新範本
              </Button>
            </div>
            {saved ? (
              <p role="status" className="m-0 text-xs text-[var(--ok)]">
                {`已存成範本「${saved}」,上面的清單裡就有了。`}
              </p>
            ) : null}
          </div>
        </div>
      )}
    </Dialog>
  );
}
